import { AnimatePresence, motion } from "framer-motion";
import { FiClock, FiMail, FiMessageCircle, FiUser } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useContact } from '../../contexts/ContactContext';

const RecentMessages = () => {
  const { messages } = useContact();
  const recentMessages = messages?.slice(0, 5) || [];

  if (!recentMessages.length) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <motion.div
          animate={{ 
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3
          }}
          className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center mb-4 shadow-lg"
        >
          <FiMail className="h-8 w-8 text-white" />
        </motion.div>
        <p className="text-gray-500 font-medium">
          No messages yet
        </p>
        <p className="text-sm text-gray-400 mt-1">
          Messages will appear here when received
        </p>
      </motion.div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'from-blue-500 to-cyan-500';
      case 'read':
        return 'from-gray-500 to-gray-600';
      case 'replied':
        return 'from-green-500 to-emerald-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <FiMail className="h-3 w-3" />;
      case 'read':
        return <FiUser className="h-3 w-3" />;
      case 'replied':
        return <FiMessageCircle className="h-3 w-3" />;
      default:
        return <FiMail className="h-3 w-3" />;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      <Link to="/admin/messages">
      <AnimatePresence>
        {recentMessages.map((message, index) => (
          <motion.div
            key={message.id}
            variants={itemVariants}
            layout
            whileHover={{ 
              scale: 1.02,
              transition: { type: "spring", stiffness: 400, damping: 25 }
            }}
            className="group relative overflow-hidden rounded-xl backdrop-blur-sm bg-white/60 border border-white/20 p-4 hover:shadow-lg transition-all duration-300"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${getStatusColor(message.status)} opacity-60`} />
            
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center shadow-md flex-shrink-0">
                    <FiUser className="h-5 w-5 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors duration-200 truncate">
                      {message.name}
                    </h4>
                    <p className="text-sm text-gray-500 truncate">
                      {message.email}
                    </p>
                  </div>
                </div>
                
                <motion.span
                  whileHover={{ scale: 1.05 }}
                  className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getStatusColor(message.status)} text-white shadow-sm flex-shrink-0`}
                >
                  {getStatusIcon(message.status)}
                  <span className="ml-1 capitalize">{message.status}</span>
                </motion.span>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                  {message.message}
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center text-xs text-gray-500">
                  <FiClock className="h-3 w-3 mr-1" />
                  {new Date(message.createdAt || Date.now()).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-3 py-1 text-xs font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Reply
                </motion.button>
              </div>
            </div>
            
            {message.status === 'new' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-2 right-2 w-3 h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full shadow-lg"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-full h-full bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"
                />
              </motion.div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
      </Link>
    </motion.div>
  );
};

export default RecentMessages;